/** What the layer knows about one clicked feature, as key-value rows (frontend-design.md §7). */
import { ActionIcon, Card, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import type { Geometry } from 'geojson';
import type { ReactNode } from 'react';
import type { FeatureTitle } from './featureTitle';
import { FIELD_FIELDS, HEALTH_FIELDS, MLS_FIELDS, TREE_FIELDS } from './layers';

export interface FeatureTarget {
  objectId: string;
  properties: Record<string, unknown>;
  /** Pixel position of the click inside the map canvas. */
  point: { x: number; y: number };
  /** The clicked feature as MapLibre addresses its state, when it has an id. */
  feature?: { source: string; sourceLayer?: string; id: string | number };
  /** The clicked feature's geometry as the map holds it (in tiles, perhaps cut at a tile edge). */
  geometry?: Geometry | null;
}

export interface FeaturePopupProps {
  target: FeatureTarget;
  onClose: () => void;
  /** What was clicked (featureTitle.ts); a plain "feature" without it. */
  title?: FeatureTitle;
  /** More below the attributes, such as a tree's side view. */
  extra?: ReactNode;
  /** The card's width in pixels (240 by default); still never wider than the map. */
  width?: number;
}

interface Row {
  key: string;
  label: string;
  text: string;
}

export const FEATURE_POPUP_WIDTH = 240;

export function FeaturePopup({ target, onClose, title, extra, width = FEATURE_POPUP_WIDTH }: FeaturePopupProps) {
  const { t, i18n } = useTranslation('viewer');
  const { number } = useFormat();

  const measure = (field: { prop: string; scale: number; unitKey: string }, digits: number): Row | null => {
    const raw = target.properties[field.prop];
    if (typeof raw !== 'number') return null;
    return { key: field.prop, label: t(`feature.props.${field.prop}`), text: `${number(raw * field.scale, digits)}\u00a0${t(field.unitKey)}` };
  };

  const props = target.properties;
  const id = props.id ?? props[FIELD_FIELDS.id];
  const genus = target.properties[TREE_FIELDS.genus];
  // forun names the genus in Czech too (forun-api.md §5.5); an English reader keeps the Latin alone.
  const czech = i18n.language.startsWith('cs') ? target.properties[TREE_FIELDS.genusCzech] : undefined;
  const genusText = typeof czech === 'string' && czech && czech !== genus ? `${String(genus)} (${czech})` : genus;
  const rows = [
    id === undefined || id === null ? null : { key: 'id', label: t('feature.props.id'), text: String(id) },
    typeof genus === 'string' && genus ? { key: 'genus', label: t('feature.props.genus'), text: String(genusText) } : null,
    measure(TREE_FIELDS.height, 2),
    measure(TREE_FIELDS.dbh, 0),
    ...volumeRows(),
    // An MLS segment's allometric volume is a model internal, not shown; it still colours by volume.
    measure(MLS_FIELDS.confidence, 0),
    measure(MLS_FIELDS.invalid, 0),
    ...fieldRows(),
    ...healthRows(),
    ...hotspotRows(),
  ].filter((row): row is Row => row !== null);

  /** A photo crown's visible volume, the volume below it and, when both are known, their sum. */
  function volumeRows(): (Row | null)[] {
    const visible = measure(TREE_FIELDS.volume, 2);
    const under = measure(TREE_FIELDS.underVolume, 2);
    const v = props[TREE_FIELDS.volume.prop];
    const u = props[TREE_FIELDS.underVolume.prop];
    const total = visible && under && typeof v === 'number' && typeof u === 'number' ? { key: 'total_volume', label: t('feature.props.total_volume'), text: `${number(v + u, 2)}\u00a0${t(TREE_FIELDS.volume.unitKey)}` } : null;
    return [visible, under, total];
  }

  /** A field crew's trunk: DBH in cm, the height with where it came from, the status, the note and the flags. */
  function fieldRows(): (Row | null)[] {
    const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    const height = measure(FIELD_FIELDS.height, 1);
    const source = text(props[FIELD_FIELDS.heightSource]);
    const status = text(props[FIELD_FIELDS.status]);
    const note = text(props[FIELD_FIELDS.note]);
    const flags = FIELD_FIELDS.flags.filter((f) => props[f] === true || props[f] === 1).map((f) => t(`feature.flags.${f}`));
    return [
      measure(FIELD_FIELDS.dbh, 0),
      height && source ? { ...height, text: `${height.text} (${t(`feature.heightSource.${source}`, { defaultValue: source })})` } : height,
      status ? { key: 'status', label: t('feature.props.status'), text: status } : null,
      flags.length ? { key: 'flags', label: t('feature.props.flags'), text: flags.join(', ') } : null,
      note ? { key: 'note', label: t('feature.props.note'), text: note } : null,
    ];
  }

  /** A crown health screen's verdict on a crown: its category, stress, crown-top indices and what it was compared with. */
  function healthRows(): (Row | null)[] {
    const category = props[HEALTH_FIELDS.category];
    if (typeof category !== 'string' || !category) return [];
    const plain = (prop: string, digits: number): Row | null => {
      const raw = props[prop];
      return typeof raw === 'number' ? { key: prop, label: t(`feature.props.${prop}`), text: number(raw, digits) } : null;
    };
    const reference = props[HEALTH_FIELDS.reference];
    return [
      { key: 'category', label: t('feature.props.category'), text: t(`legend.health.${category}`, { defaultValue: category }) },
      plain(HEALTH_FIELDS.stress, 2),
      plain(HEALTH_FIELDS.ndvi, 3),
      plain(HEALTH_FIELDS.ndre, 3),
      typeof reference === 'string' && reference ? { key: 'reference', label: t('feature.props.reference'), text: t(`feature.references.${reference}`, { defaultValue: reference }) } : null,
      measure(HEALTH_FIELDS.distance, 0),
    ];
  }

  /** A hotspot of dead, snag and declining crowns: how many of each, the genera, the volume and the area. */
  function hotspotRows(): (Row | null)[] {
    if (props[HEALTH_FIELDS.hotspot] === undefined) return [];
    const count = (prop: string): Row | null => (typeof props[prop] === 'number' ? { key: prop, label: t(`feature.props.${prop}`), text: number(props[prop] as number) } : null);
    const genera = props.genera;
    return [
      count('n_flagged'),
      count('dead'),
      count('snag'),
      count('declining'),
      count('threatened'),
      typeof genera === 'string' && genera ? { key: 'genera', label: t('feature.props.genera'), text: genera } : null,
      measure({ prop: 'volume_m3', scale: 1, unitKey: 'units.m3' }, 1),
      measure({ prop: 'area_m2', scale: 1, unitKey: 'units.m2' }, 0),
    ];
  }

  const level = title?.values?.level;
  const heading = !title
    ? t('feature.title')
    : title.key === 'unit' && level === 'area'
      ? t('feature.levels.area')
      : t(`feature.titles.${title.key}`, { ...title.values, level: level ? t(`feature.levels.${level}`, { defaultValue: level }) : '' });

  return (
    <Card
      padding="sm"
      radius="lg"
      shadow="md"
      w={width}
      maw="100%"
      data-testid="feature-popup"
    >
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Text fz={11} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
            {heading}
          </Text>
          <ActionIcon size="sm" variant="subtle" aria-label={t('feature.close')} onClick={onClose}>
            ×
          </ActionIcon>
        </Group>
        {rows.map((row) => (
          // A long label wraps onto a second line rather than running into its value.
          <Group key={row.key} justify="space-between" align="flex-start" gap="xs" wrap="nowrap" style={{ lineHeight: 1.3 }}>
            <Text fz={12} c={brand.textMuted}>
              {row.label}
            </Text>
            <Text fz={12} fw={600}>
              {row.text}
            </Text>
          </Group>
        ))}
        {extra}
      </Stack>
    </Card>
  );
}
